import React from 'react';
import { Building2, TrendingUp, Globe, DollarSign, ArrowRight, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Corporate = () => {
  const services = [
    {
      icon: Building2,
      title: "Corporate Accounts",
      description: "Comprehensive business banking solutions with flexible account management and dedicated support."
    },
    {
      icon: TrendingUp,
      title: "Treasury Management",
      description: "Optimize your cash flow with our advanced treasury management and investment solutions."
    },
    {
      icon: Globe,
      title: "Trade Finance",
      description: "Facilitate international trade with our comprehensive trade finance and foreign exchange services."
    },
    {
      icon: DollarSign,
      title: "Business Loans",
      description: "Access competitive financing options to fuel your business growth and expansion plans."
    }
  ];

  const industries = [
    { name: "Manufacturing", image: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4" },
    { name: "Technology", image: "https://images.unsplash.com/photo-1616587656977-ac36a5a430bc" },
    { name: "Healthcare", image: "https://images.unsplash.com/photo-1686771416282-3888ddaf249b" },
    { name: "Retail", image: "https://images.pexels.com/photos/3531895/pexels-photo-3531895.jpeg" },
    { name: "Real Estate", image: "https://images.pexels.com/photos/351264/pexels-photo-351264.jpeg" },
    { name: "Professional Services", image: "https://images.pexels.com/photos/50987/money-card-business-credit-card-50987.jpeg" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">Corporate Banking</h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Empowering businesses with comprehensive financial solutions — including corporate accounts, treasury management, and trade finance — to help organizations thrive locally and globally.
              </p>
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4"
                alt="Corporate Banking"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Corporate Solutions</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Tailored banking services designed to meet the unique needs of your business
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-all duration-300 group border-0 shadow-md">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                    <service.icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Morgan Guaranty Trust?</h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We understand the complexities of modern business and provide the financial infrastructure you need to succeed.
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Dedicated Relationship Manager</h3>
                    <p className="text-gray-600">Get personalized support from a dedicated banking professional who understands your business.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Global Reach</h3>
                    <p className="text-gray-600">Access international markets with our extensive network and trade finance expertise.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Growth Partnership</h3>
                    <p className="text-gray-600">We grow with you, providing scalable solutions that adapt to your evolving needs.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1686771416282-3888ddaf249b"
                alt="Business Partnership"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Specialized banking solutions across diverse sectors
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
                  <h3 className="text-white text-2xl font-bold">{industry.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cash Management Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Advanced Cash Management</h2>
              <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                Streamline your financial operations with our comprehensive cash management solutions designed for modern businesses.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Real-time transaction monitoring and reporting</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Automated payment processing and reconciliation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Multi-currency account management</span>
                </li>
              </ul>
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3">
                Learn More
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1616587656977-ac36a5a430bc"
                alt="Cash Management"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Let's Build Your Business Together</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact our corporate banking team to discuss how we can support your business goals
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
            Schedule a Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Corporate;
